import { useState, type FormEvent } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { Button, FormField, LoadingSkeleton, TextInput } from "@a2z/ui";
import { FirstLoginPasswordError } from "@/lib/api/client";
import { resolvePublicSellerRedirectPath } from "@/lib/auth/public-seller-auth";
import {
  firstPasswordNeedsCurrent,
  markSignedInBefore,
} from "@/modules/auth/lib/first-visit";
import { keys } from "@/lib/i18n/portal";
import { useTranslations } from "@/lib/i18n/intl";
import { useLocale } from "@/app/locale";
import { useSessionQuery } from "@/shared/services/useSessionQuery";
import { useReplaceFirstLoginPasswordMutation } from "../services/useReplaceFirstLoginPasswordMutation";
import { getPrivateSessionState } from "@/lib/api/private-session";
import { safeAuthReturnPath, withAuthReturnPath } from "@/lib/auth/safe-return";

export function FirstLoginPasswordPage() {
  const locale = useLocale();
  const t = useTranslations();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const safeNext =
    params.getAll("next").length === 1
      ? safeAuthReturnPath(params.get("next"))
      : null;
  const { data, isLoading, error } = useSessionQuery({
    storeSlug: "password-change",
  });
  const replacement = useReplaceFirstLoginPasswordMutation();
  const [currentPassword, setCurrentPassword] = useState("");
  // The temporary password is asked for only if the backend still needs it
  // (an older one answers 400): the session already proves the seller has it.
  const [needsCurrentState, setNeedsCurrent] = useState(false);
  // Read per render: the refusal that sets it lands while the form remounts.
  const needsCurrent = needsCurrentState || firstPasswordNeedsCurrent();
  const [nextPassword, setNextPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [failure, setFailure] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  if (isLoading)
    return (
      <div className="grid min-h-svh place-items-center bg-[color:var(--canvas)] p-6">
        <div role="status" aria-busy="true" className="w-full max-w-md">
          <span className="sr-only">{t(keys.common.loading)}</span>
          <LoadingSkeleton variant="card" rows={4} />
        </div>
      </div>
    );
  if (error || !data)
    return (
      <Navigate to={withAuthReturnPath(`/${locale}/login`, safeNext)} replace />
    );
  // This page serves only the FORCED first-login change. Anyone else who lands
  // here wants an ordinary password change: send them to the account page that
  // offers one, not to the store root (which, for paused staff, is the very
  // screen that linked here — a loop).
  if (!data.mustChangePassword) {
    return (
      <Navigate
        to={safeNext ?? `/${locale}/${data.seller.storeSlug}/profile`}
        replace
      />
    );
  }

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (replacement.isPending) return;
    setFailure(null);
    if (nextPassword.length < 8 || nextPassword !== confirmPassword) {
      setFailure(t(keys.auth.firstPasswordValidation));
      return;
    }
    const initialGeneration = getPrivateSessionState().generation;
    const expectedGeneration = initialGeneration + 1;
    const current = () => {
      const session = getPrivateSessionState();
      return !session.changing && session.generation === expectedGeneration;
    };
    setSaving(true);
    try {
      const result = await replacement.mutateAsync({
        currentPassword: needsCurrent ? currentPassword : undefined,
        nextPassword,
      });
      if (!current()) return;
      markSignedInBefore();
      if (result?.redirectPath) {
        // Still signed in (A6): straight into the store. The "saved" toast is
        // the locale layout's (lib/auth/password-saved-notice): this page does
        // not outlive the session rebind.
        navigate(
          safeNext ??
            resolvePublicSellerRedirectPath(locale, result.redirectPath),
          { replace: true },
        );
        return;
      }
      navigate(
        withAuthReturnPath(`/${locale}/login?notice=password-saved`, safeNext),
        { replace: true },
      );
    } catch (caught) {
      const session = getPrivateSessionState();
      if (
        !current() &&
        (session.changing || session.generation !== initialGeneration)
      )
        return;
      if (
        !needsCurrent &&
        caught instanceof FirstLoginPasswordError &&
        caught.status === 400 &&
        /currentpassword/i.test(caught.message)
      ) {
        setNeedsCurrent(true);
        setFailure(null);
        return;
      }
      setFailure(t(keys.auth.firstPasswordFailed));
    } finally {
      if (
        current() ||
        getPrivateSessionState().generation === initialGeneration
      )
        setSaving(false);
    }
  };

  return (
    <main className="grid min-h-svh place-items-center bg-[color:var(--canvas)] p-6">
      <form
        onSubmit={submit}
        className="flex w-full max-w-md flex-col gap-4 rounded-xl bg-[color:var(--surface)] p-6 shadow-lg"
      >
        <h1 className="text-2xl font-bold">
          {t(keys.auth.firstPasswordTitle)}
        </h1>
        <p className="text-sm text-[color:var(--text-muted)]">
          {t(keys.auth.firstPasswordBodyShort)}
        </p>
        {needsCurrent ? (
          <FormField
            name="currentPassword"
            label={t(keys.auth.temporaryPasswordLabel)}
          >
            <TextInput
              name="currentPassword"
              type="password"
              autoComplete="current-password"
              required
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
            />
          </FormField>
        ) : null}
        <FormField name="nextPassword" label={t(keys.auth.newPasswordLabel)}>
          <TextInput
            name="nextPassword"
            type="password"
            autoComplete="new-password"
            required
            value={nextPassword}
            onChange={(event) => setNextPassword(event.target.value)}
          />
        </FormField>
        <FormField
          name="confirmPassword"
          label={t(keys.auth.confirmPasswordLabel)}
        >
          <TextInput
            name="confirmPassword"
            type="password"
            autoComplete="new-password"
            required
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
          />
        </FormField>
        {failure || (replacement.replacementFailed && !needsCurrent) ? (
          <p role="alert" className="text-sm text-[color:var(--danger)]">
            {failure ?? t(keys.auth.firstPasswordFailed)}
          </p>
        ) : null}
        <Button
          type="submit"
          variant="primary"
          isLoading={saving || replacement.isPending}
        >
          {t(keys.auth.saveNewPassword)}
        </Button>
      </form>
    </main>
  );
}
